import { useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sparkles, Loader2, Check, Gift } from "lucide-react";

export default function Signup() {
  const { signup } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const ref = searchParams.get("ref") || "";
  const plan = searchParams.get("plan") || "";
  const [form, setForm] = useState({ company_name: "", name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await signup({ ...form, ref });
      const planQ = ["starter", "premium"].includes(plan) ? `&plan=${plan}` : "";
      navigate(`/owner/billing?welcome=1${planQ}`);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Could not create account");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-primary text-primary-foreground">
        <Link to="/" className="flex items-center gap-2">
          <div className="bg-white rounded-xl p-1.5">
            <img src="/logo.png" alt="SmartHost" className="h-8 w-8 object-contain" />
          </div>
          <span className="font-display text-2xl font-bold">SmartHost Solutions</span>
        </Link>
        <div className="space-y-5">
          <h1 className="font-display text-4xl font-semibold leading-tight">
            Start managing turnovers in minutes.
          </h1>
          <ul className="space-y-3 text-primary-foreground/90">
            {["Free 3-day trial", "Then from $39/mo + $10 per extra property", "Cancel anytime"].map(
              (t) => (
                <li key={t} className="flex items-center gap-2">
                  <Check className="h-5 w-5" /> {t}
                </li>
              )
            )}
          </ul>
        </div>
        <div className="text-sm text-primary-foreground/70">Smart Systems. Seamless Stays.</div>
      </div>

      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <img src="/logo.png" alt="SmartHost" className="h-9 w-9 object-contain" />
            <span className="font-display text-xl font-bold">SmartHost Solutions</span>
          </div>
          <h2 className="font-display text-2xl font-semibold mb-1">Create your account</h2>
          <p className="text-muted-foreground text-sm mb-8">Get started with your free 3-day trial</p>
          {ref && (
            <div
              className="flex items-center gap-2 rounded-lg bg-secondary text-secondary-foreground px-4 py-3 mb-6 text-sm"
              data-testid="referral-banner"
            >
              <Gift className="h-4 w-4 text-primary shrink-0" />
              You were invited by a SmartHost partner — welcome aboard!
            </div>
          )}
          <form onSubmit={submit} className="space-y-4" data-testid="signup-form">
            <div className="space-y-2">
              <Label>Company name</Label>
              <Input
                value={form.company_name}
                onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                placeholder="Sparkle Cleaning Co."
                required
                data-testid="signup-company-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Your name</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
                data-testid="signup-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
                data-testid="signup-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Password</Label>
              <Input
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                required
                minLength={6}
                data-testid="signup-password-input"
              />
            </div>
            {error && <p className="text-sm text-destructive" data-testid="signup-error">{error}</p>}
            <Button type="submit" className="w-full h-11 text-base" disabled={loading} data-testid="signup-submit">
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Create account
            </Button>
          </form>
          <p className="text-sm text-muted-foreground mt-6 text-center">
            Already have an account?{" "}
            <Link to="/login" className="text-primary hover:underline font-medium">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
